import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { faqService, FAQ as FAQType } from '../services/faqService';
import PageLoader from '../components/PageLoader';
import CardSkeleton from '../components/CardSkeleton';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import heroData from '../data/heroData.json';

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [faqs, setFaqs] = useState<FAQType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch all active FAQs with a high limit to get all of them
        const response = await faqService.getFAQs({ limit: 1000, isActive: true });
        
        if (response.success && response.data) {
          // Handle both array and paginated response
          const faqsData = Array.isArray(response.data) 
            ? response.data 
            : (response.data as any).faqs || [];
          
          // Sort by order, then by createdAt
          const sortedFaqs = faqsData.sort((a: FAQType, b: FAQType) => {
            if (a.order !== b.order) {
              return a.order - b.order;
            }
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          
          setFaqs(sortedFaqs);
        }
      } catch (err: any) {
        console.error('Error fetching FAQs:', err);
        setError(err.message || 'Failed to load FAQs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Group FAQs by category
  const groupFAQsByCategory = (faqs: FAQType[]) => {
    const grouped: { [key: string]: FAQType[] } = {};
    
    faqs.forEach((faq) => {
      const category = faq.category || 'General Questions';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(faq);
    });

    // Convert to array format
    return Object.entries(grouped).map(([title, faqs]) => ({
      title,
      faqs,
    }));
  };

  const faqCategories = groupFAQsByCategory(faqs);

  // Show full page loader on initial load
  if (loading && faqs.length === 0) {
    return (
      <>
        <SEO
          title="Frequently Asked Questions - Yoga Teacher Training FAQ"
          description="Find answers to common questions about our yoga teacher training programs, accommodation, and the spiritual journey that awaits you in Rishikesh."
          keywords="yoga teacher training faq, yoga certification questions, yoga training questions, yoga school faq, yoga teacher training rishikesh"
          url="https://bookmyyogattc.com/faq"
          canonical="https://bookmyyogattc.com/faq"
        />
        <div className="pt-20">
        <HeroSection
          title={heroData.faq.title}
          description={heroData.faq.description}
          patternId="faqPattern"
        />
        <PageLoader message="Loading FAQs..." />
        </div>
      </>
    );
  }

  // Create FAQPage structured data
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions - Yoga Teacher Training FAQ"
        description="Find answers to common questions about our yoga teacher training programs, accommodation, and the spiritual journey that awaits you in Rishikesh."
        keywords="yoga teacher training faq, yoga certification questions, yoga training questions, yoga school faq, yoga teacher training rishikesh"
        url="https://bookmyyogattc.com/faq"
        canonical="https://bookmyyogattc.com/faq"
        structuredData={faqStructuredData}
      />
      <div className="pt-20">
      <HeroSection
        title={heroData.faq.title}
        description={heroData.faq.description}
        patternId="faqPattern"
      />

      {/* FAQ Section */}
      <section className="py-20 bg-fog">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-section font-serif font-light mb-6 tracking-wide text-midnight">
              Everything You Need to Know
            </h2>
            <p className="text-5 text-midnight/70 max-w-3xl mx-auto font-light">
              We've compiled answers to the most common questions about our programs. 
              If you don't find what you're looking for, please don't hesitate to contact us.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-12">
            {loading ? (
              <>
                <CardSkeleton count={5} type="faq" />
              </>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 text-4 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-forest text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : faqCategories.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-midnight/70 text-4">No FAQs available at the moment.</p>
              </div>
            ) : (
              faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <div className="bg-forest text-white p-6">
                  <h3 className="text-5 font-serif font-light">{category.title}</h3>
                </div>
                
                <div className="p-6">
                  {category.faqs.map((faq, faqIndex) => {
                    const globalIndex = categoryIndex * 1000 + faqIndex;
                    return (
                      <div key={faq._id || faqIndex} className="border-b border-midnight/10 last:border-b-0">
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full py-6 px-6 bg-forest text-white flex justify-between items-center text-left hover:brightness-110 transition-all duration-300"
                        >
                          <h4 className="text-4 font-medium pr-4">
                            {faq.question}
                          </h4>
                          <div className={`text-5 transition-transform duration-300 flex-shrink-0 ${
                            openFAQ === globalIndex ? 'rotate-45' : ''
                          }`}>
                            +
                          </div>
                        </button>
                        
                        <motion.div
                          initial={false}
                          animate={{
                            height: openFAQ === globalIndex ? 'auto' : 0,
                            opacity: openFAQ === globalIndex ? 1 : 0
                          }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-6">
                            <p className="text-midnight/70 font-light leading-relaxed whitespace-pre-line">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-forest text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5 md:text-5 font-serif font-light mb-6 tracking-wide">
              Still Have Questions?
            </h2>
            <p className="text-5 opacity-95 mb-8 max-w-3xl mx-auto font-light leading-relaxed">
              Our team is here to help you on your yoga journey. 
              Reach out to us for personalized guidance and support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="/contact">
                <button className="bg-white text-forest px-8 py-4 rounded-lg font-semibold text-4 hover:bg-opacity-90 transition-colors duration-200 shadow-lg transform hover:scale-105 active:scale-95">
                  Contact Us
                </button>
              </a>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer">
                <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-4 hover:bg-white hover:text-forest transition-colors duration-200 transform hover:scale-105 active:scale-95">
                  WhatsApp Us
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  );
};

export default FAQ; 